/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Detector for Node.js API frameworks (Express, Fastify, Koa, etc.)
 */
import type { DetectorResult } from '../types/index.js';
import type { Detector } from './base.js';
import { readPackageJson, hasAnyDependency, findFiles } from '../utils/fs.js';
import { detectPackageManager, getRunCommand } from '../utils/pm.js';
import { createDetectorResult } from './base.js';

export class NodeApiDetector implements Detector {
  name = 'node-api';

  async detect(cwd: string): Promise<DetectorResult> {
    const pkg = await readPackageJson(cwd);
    if (!pkg) {
      return createDetectorResult(this.name, 0);
    }

    const pm = detectPackageManager(cwd);
    const scripts = (pkg.scripts as Record<string, string>) || {};

    // Check for common API frameworks
    const hasExpress = await hasAnyDependency(cwd, ['express']);
    const hasFastify = await hasAnyDependency(cwd, ['fastify']);
    const hasKoa = await hasAnyDependency(cwd, ['koa']);
    const hasNest = await hasAnyDependency(cwd, ['@nestjs/core']);
    const hasHapi = await hasAnyDependency(cwd, ['@hapi/hapi', 'hapi']);

    let detectedFramework: string | null = null;
    let confidence = 0;

    if (hasNest) {
      detectedFramework = 'NestJS';
      confidence = 90;
    } else if (hasFastify) {
      detectedFramework = 'Fastify';
      confidence = 85;
    } else if (hasExpress) {
      detectedFramework = 'Express';
      confidence = 80;
    } else if (hasKoa) {
      detectedFramework = 'Koa';
      confidence = 80;
    } else if (hasHapi) {
      detectedFramework = 'Hapi';
      confidence = 80;
    }

    // Look for common API entry points
    const apiEntryPoints = await findFiles(
      ['src/server.{ts,js}', 'src/app.{ts,js}', 'src/index.{ts,js}', 'server.{ts,js}', 'app.{ts,js}', 'index.{ts,js}'],
      cwd
    );

    if (detectedFramework && apiEntryPoints.length > 0) {
      // Look for dev or start script
      const devScript = scripts.dev || scripts['start:dev'];
      const startScript = scripts.start;

      if (devScript) {
        const { cmd, args } = getRunCommand(pm, devScript === scripts.dev ? 'dev' : 'start:dev');
        return createDetectorResult(this.name, confidence, {
          cwd,
          pm,
          cmd,
          args,
          detectedType: `${detectedFramework} API`,
          reasons: [
            `Detected ${detectedFramework} API project`,
            `Found entry point: ${apiEntryPoints[0]}`,
            `Using package manager: ${pm}`,
          ],
        });
      }

      if (startScript) {
        const { cmd, args } = getRunCommand(pm, 'start');
        return createDetectorResult(this.name, confidence - 5, {
          cwd,
          pm,
          cmd,
          args,
          detectedType: `${detectedFramework} API`,
          reasons: [
            `Detected ${detectedFramework} API project`,
            `Found entry point: ${apiEntryPoints[0]}`,
            `Using package manager: ${pm}`,
          ],
        });
      }

      // Fallback to direct node execution
      if (apiEntryPoints.length > 0) {
        return createDetectorResult(this.name, confidence - 10, {
          cwd,
          pm,
          cmd: 'node',
          args: [apiEntryPoints[0]],
          detectedType: `${detectedFramework} API`,
          reasons: [
            `Detected ${detectedFramework} API project`,
            `Entry point: ${apiEntryPoints[0]}`,
            'No dev/start script found, using direct node execution',
          ],
        });
      }
    }

    return createDetectorResult(this.name, 0);
  }
}

